import React, { useEffect, useState } from "react";
import {
    Flex,
    Box,
    Text,
} from "@chakra-ui/react";
import {
    EllipsisVertical,
    Pencil,
    Eye,
    Trash2,
} from "lucide-react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import ReusableTable from "components/Tables/ReusableTable";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";

import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

import {
    fetchProgressionConfigurationsAction,
    deleteProgressionConfigurationAction,
} from "state/actions/agentProgressionActions";

import {
    useCustomNotification,
} from "components/Toasts/OpenNotification";

import AddProgressionConfiguration from "./components/AddProgressionConfiguration";
import ViewProgressionConfiguration from "./components/ViewProgressionConfiguration";
import DeleteProgressionConfiguration from "./components/DeleteProgressionConfiguration";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";


const AgentProgressionConfiguration = () => {

    const dispatch = useDispatch();

    const notify = useCustomNotification();


    const {
        configurations,
        loading,
        error,
    } = useSelector(
        (state) =>
            state.agentProgression || {}
    );


    const tableData = Array.isArray(
        configurations
    )
        ? configurations
        : [];


    const [
        isAddOpen,
        setIsAddOpen,
    ] = useState(false);


    const [
        isEditOpen,
        setIsEditOpen,
    ] = useState(false);


    const [
        isViewOpen,
        setIsViewOpen,
    ] = useState(false);


    const [
        isDeleteOpen,
        setIsDeleteOpen,
    ] = useState(false);


    const [
        selectedConfiguration,
        setSelectedConfiguration,
    ] = useState(null);


    useEffect(() => {

        dispatch(
            fetchProgressionConfigurationsAction()
        );

    }, [dispatch]);


    const handleView = (
        configuration
    ) => {

        setSelectedConfiguration(
            configuration
        );

        setIsViewOpen(true);

    };


    const handleEdit = (
        configuration
    ) => {

        setSelectedConfiguration(
            configuration
        );

        setIsEditOpen(true);

    };


    const handleDelete = (
        configuration
    ) => {

        setSelectedConfiguration(
            configuration
        );

        setIsDeleteOpen(true);

    };


    const handleCloseView = () => {

        setSelectedConfiguration(null);

        setIsViewOpen(false);

    };


    const handleCloseEdit = () => {

        setSelectedConfiguration(null);

        setIsEditOpen(false);

    };


    const handleCloseDelete = () => {

        setSelectedConfiguration(null);

        setIsDeleteOpen(false);

    };


    const handleConfirmDelete = async () => {

        if (
            !selectedConfiguration
                ?.configuration_id
        ) {
            return;
        }


        const response =
            await dispatch(
                deleteProgressionConfigurationAction(
                    selectedConfiguration.configuration_id
                )
            );


        if (response?.success) {

            notify({
                message:
                    "Progression configuration deleted successfully.",
                type: "success",
            });


            setIsDeleteOpen(false);

            setSelectedConfiguration(
                null
            );


            dispatch(
                fetchProgressionConfigurationsAction()
            );

        } else {

            notify({
                message:
                    response?.error ||
                    "Failed to delete progression configuration.",
                type: "error",
            });

        }

    };


    const generateMenuItems = (
        row
    ) => [

        {
            icon: (
                <Eye size={16} />
            ),

            text: "View/Edit Configuration",

            onClick: () =>
                handleView(
                    row.original
                ),
        },



        {
            icon: (
                <Trash2 size={16} />
            ),

            text: "Delete Configuration",

            onClick: () =>
                handleDelete(
                    row.original
                ),
        },

    ];


    const columnsData = [

        {
            Header: "Level",

            accessor: "level",
        },


        {
            Header: "Experience (Months)",

            accessor:
                "minimum_experience_months",
        },


        {
            Header: "Package Volume",

            accessor:
                "minimum_package_volume",
        },


        {
            Header: "Customer Rating",

            accessor:
                "minimum_customer_rating",
        },


        {
            Header: "Compliance Score",

            accessor:
                "minimum_compliance_score",
        },


        {
            Header: "Training Required",

            accessor:
                "training_completion_required",

            Cell: ({
                value,
            }) => (

                <StatusBadge
                    label={
                        value
                            ? "Required"
                            : "Not Required"
                    }

                    variant={
                        value
                            ? "success"
                            : "gray"
                    }
                />

            ),
        },


        {
            Header: "Status",

            accessor: "is_active",

            Cell: ({
                value,
            }) => {

                const status =
                    value
                        ? "Active"
                        : "Inactive";


                return (

                    <StatusBadge
                        label={status}

                        variant={
                            getStatusVariant(
                                status
                            )
                        }
                    />

                );

            },
        },


        {
            Header: "Actions",

            accessor: "moreOptions",

            Cell: ({
                row,
            }) => (

                <ThreeDotMenu

                    icon={
                        <EllipsisVertical
                            size={20}
                            color="grey"
                        />
                    }

                    items={
                        generateMenuItems(
                            row
                        )
                    }

                />

            ),
        },

    ];


    return (

        <Flex
            flexDirection="column"
            pt={{
                base: "120px",
                md: "25px",
            }}
        >

            {/* HEADER */}

            <Box p={6}>

                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                >

                    <Box>

                        <Text
                            fontSize="md"
                            fontWeight="bold"
                        >

                            {loading ? (

                                <Skeleton
                                    height="20px"
                                    width="280px"
                                />

                            ) : (

                                `Agent Progression Configurations: ${tableData.length}`

                            )}

                        </Text>



                    </Box>


                    <GradientButton
                        onClick={() =>
                            setIsAddOpen(true)
                        }
                    >
                        Add Configuration
                    </GradientButton>

                </Flex>

            </Box>


            {/* TABLE */}

            <ReusableTable

                columnsData={
                    columnsData
                }

                tableData={
                    tableData
                }

                isLoading={
                    loading
                }

                error={
                    error
                }

                emptyMessage={
                    "No progression configurations found."
                }

            />


            {/* ADD */}

            <AddProgressionConfiguration

                isOpen={
                    isAddOpen
                }

                onClose={() =>
                    setIsAddOpen(false)
                }

            />


         


            {/* VIEW */}

            <ViewProgressionConfiguration

                isOpen={
                    isViewOpen
                }

                onClose={
                    handleCloseView
                }

                configuration={
                    selectedConfiguration
                }

            />


            {/* DELETE */}

            <DeleteProgressionConfiguration

                isOpen={
                    isDeleteOpen
                }

                onClose={
                    handleCloseDelete
                }

                onConfirm={
                    handleConfirmDelete
                }

                configurationName={
                    selectedConfiguration
                        ?.level || ""
                }

            />

        </Flex>

    )

};


export default AgentProgressionConfiguration;